import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import {
  GST_RATE, fmtINR, fmtInvDate, calcInvoice, invoiceDbFields, useAmountPaid, overpaidMessage,
  invPaid, invBalance, invDate, invLines, invDiscount, invGst, normStatus, printInvoice, sendInvoiceWhatsApp,
} from '../lib/billing'
import { friendlyError } from '../lib/permissions'

// invoice_number is assigned by a DB trigger on INSERT (format MD/26-27/0001) and
// is never sent from the app. Amount/discount/GST maths lives in lib/billing.js.

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  teal950: '#082828', teal900: '#0d3d3d', teal800: '#0f5454',
  teal700: '#126868', teal600: '#148080', teal500: '#19a8a8',
  teal400: '#2ecece', teal300: '#5fd4d4', teal200: '#90e0e0', teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray800: '#1e293b', gray900: '#0f172a',
  red50: '#fff1f2', red100: '#ffe4e6', red600: '#dc2626',
  green50: '#f0fdf4', green100: '#dcfce7', green600: '#16a34a',
  amber50: '#fffbeb', amber100: '#fef3c7', amber600: '#d97706',
  blue50: '#eff6ff', blue600: '#2563eb',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

// ─── Service catalogue ─────────────────────────────────────────────────────────
const SERVICES = [
  { name: 'Consultation',    rate: 500   },
  { name: 'Follow-up',       rate: 300   },
  { name: 'Chemical Peel',   rate: 2000  },
  { name: 'HydraFacial',     rate: 3000  },
  { name: 'PRP Treatment',   rate: 5000  },
  { name: 'Laser Treatment', rate: 4000  },
  { name: 'Procedure',       rate: 2500  },
  { name: 'Botox',           rate: 8000  },
  { name: 'Dermal Filler',   rate: 10000 },
  { name: 'Microneedling',   rate: 3500  },
  { name: 'Carbon Peel',     rate: 3000  },
  { name: 'Thread Lift',     rate: 15000 },
  { name: 'Meso Therapy',    rate: 4500  },
  { name: 'Skin Brightening',rate: 2500  },
  { name: 'Other',           rate: 0     },
]

const PAYMENT_MODES = ['Cash', 'Card', 'UPI', 'Net Banking', 'Other']

// ─── Helpers ───────────────────────────────────────────────────────────────────
function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const STATUS_CFG = {
  paid:    { bg: T.green50, color: T.green600, border: '#bbf7d0', label: 'PAID'    },
  partial: { bg: T.amber50, color: T.amber600, border: '#fde68a', label: 'PARTIAL' },
  unpaid:  { bg: T.red50,   color: T.red600,   border: '#fecdd3', label: 'UNPAID'  },
}

function StatusBadge({ status }) {
  const s = STATUS_CFG[status] || STATUS_CFG.unpaid
  return (
    <span style={{
      padding: '3px 11px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      whiteSpace: 'nowrap'
    }}>{s.label}</span>
  )
}

// ─── Patient picker ─────────────────────────────────────────────────────────────
function PatientPicker({ value, onChange }) {
  const [q, setQ] = useState(value?.name || '')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const timer = useRef(null)

  useEffect(() => { setQ(value?.name || '') }, [value])

  function search(v) {
    setQ(v)
    clearTimeout(timer.current)
    if (!v.trim()) { setResults([]); setOpen(false); return }
    timer.current = setTimeout(async () => {
      const term = v.trim()
      const isPhone = /^\d{3,}$/.test(term)
      const { data } = await supabase
        .from('patients')
        .select('id, full_name, phone, age')
        .ilike(isPhone ? 'phone' : 'full_name', `%${term}%`)
        .order('full_name')
        .limit(10)
      setResults((data || []).map(p => ({ id: p.id, name: p.full_name, phone: p.phone, age: p.age })))
      setOpen(true)
    }, 280)
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={q}
        onChange={e => search(e.target.value)}
        onFocus={() => q && results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search patient by name or phone…"
        style={{
          width: '100%', padding: '9px 12px', border: `1px solid ${T.gray300}`,
          borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
          fontFamily: T.font
        }}
      />
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: T.white, border: `1px solid ${T.gray200}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto'
        }}>
          {results.map(p => (
            <div
              key={p.id}
              onMouseDown={() => { onChange(p); setOpen(false) }}
              style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 14, borderBottom: `1px solid ${T.gray100}` }}
              onMouseEnter={e => e.currentTarget.style.background = T.teal50}
              onMouseLeave={e => e.currentTarget.style.background = T.white}
            >
              <span style={{ fontWeight: 500, color: T.gray900 }}>{p.name}</span>
              {p.age != null && <span style={{ color: T.gray500, marginLeft: 8, fontSize: 13 }}>{p.age} yrs</span>}
              <span style={{ color: T.gray500, marginLeft: 10, fontSize: 13 }}>{p.phone}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Summary card ───────────────────────────────────────────────────────────────
function SummaryCard({ label, value, sub, accent, icon }) {
  return (
    <div style={{
      background: T.white, borderRadius: 12, padding: '18px 22px',
      border: `1px solid ${T.gray200}`, flex: 1, minWidth: 160,
      borderLeft: `4px solid ${accent}`,
      display: 'flex', flexDirection: 'column', gap: 4
    }}>
      <div style={{ fontSize: 12, color: T.gray500, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{icon}</span>{label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: T.gray900 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: T.gray400 }}>{sub}</div>}
    </div>
  )
}

// ─── Invoice Form (create / edit modal) ────────────────────────────────────────
function InvoiceForm({ invoice, onSave, onClose }) {
  const { userProfile, clinic } = useAuth()
  const isEdit = !!invoice?.id
  const [date, setDate]           = useState(invoice ? invDate(invoice) || todayISO() : todayISO())
  const [patient, setPatient]     = useState(
    invoice ? { id: invoice.patient_id, name: invoice.patient_name, phone: invoice.patient_phone || invoice.patients?.phone } : null
  )
  const [items, setItems]         = useState(() => {
    const lines = invoice ? invLines(invoice) : []
    return lines.length
      ? lines.map(l => ({ service: l.name, qty: l.qty, rate: l.rate, amount: l.amount }))
      : [{ service: '', qty: 1, rate: 0, amount: 0 }]
  })
  const [discType, setDiscType]   = useState(invoice?.discount_type === 'percent' ? 'percent' : 'flat')
  const [discVal, setDiscVal]     = useState(
    invoice ? (invoice.discount_value != null ? invoice.discount_value : invDiscount(invoice)) : 0
  )
  // Clinic is not GST-registered yet — new invoices start with GST off
  const [gstOn, setGstOn]         = useState(invoice ? (invoice.gst_enabled ?? invGst(invoice) > 0) : false)
  const [payMode, setPayMode]     = useState(invoice?.payment_mode || 'Cash')
  const [notes, setNotes]         = useState(invoice?.notes || '')
  const [saving, setSaving]       = useState(false)
  const [err, setErr]             = useState('')

  // Fetch patient name/phone when editing and they're missing
  useEffect(() => {
    if (!isEdit || (patient?.name && patient?.phone) || !invoice?.patient_id) return
    ;(async () => {
      const { data } = await supabase.from('patients').select('id, full_name, phone').eq('id', invoice.patient_id).maybeSingle()
      if (data) setPatient({ id: data.id, name: data.full_name, phone: data.phone })
    })()
  }, [isEdit, invoice?.patient_id]) // eslint-disable-line

  const calcArgs = { lines: items, discountType: discType, discountValue: discVal, gstEnabled: gstOn }
  const base = calcInvoice({ ...calcArgs, amountPaid: 0 })
  // On edit, an earlier partial payment is kept as a manual value; a fully-paid or new
  // invoice follows the total as items/discount/GST change.
  const existingPaid = Number(invoice?.amount_paid) || 0
  const amt  = useAmountPaid(base.total, {
    initial: existingPaid,
    manual:  isEdit && existingPaid < (Number(invoice?.total) || 0),
  })
  const calc = calcInvoice({ ...calcArgs, amountPaid: amt.value })

  function updateItem(idx, field, val) {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it
      const u = { ...it, [field]: val }
      if (field === 'service') {
        const svc = SERVICES.find(s => s.name === val)
        if (svc) { u.rate = svc.rate; u.amount = svc.rate * (Number(u.qty) || 1) }
      }
      if (field === 'qty')  u.amount = (Number(val) || 0) * (Number(u.rate) || 0)
      if (field === 'rate') u.amount = (Number(u.qty) || 0) * (Number(val) || 0)
      return u
    }))
  }

  async function save() {
    if (!patient?.id)          { setErr('Please select a patient'); return }
    if (!calc.items.length)    { setErr('Add at least one service'); return }
    if (calc.overpaid)         { setErr(overpaidMessage(calc)); return }
    setSaving(true); setErr('')

    const payload = {
      patient_id:    patient.id,
      patient_name:  patient.name,
      patient_phone: patient.phone || null,
      ...invoiceDbFields(calc, { date, paymentMode: payMode, notes }),
    }

    const { data, error } = isEdit
      ? await supabase.from('invoices').update(payload).eq('id', invoice.id).select('*, patients(phone)').single()
      : await supabase.from('invoices').insert({ ...payload, clinic_id: userProfile?.clinic_id }).select('*, patients(phone)').single()

    setSaving(false)
    if (error) { setErr(friendlyError(error)); return }
    onSave(data, isEdit)
  }

  // Shared input style
  const inp = {
    padding: '8px 10px', border: `1px solid ${T.gray300}`, borderRadius: 7,
    fontSize: 13, outline: 'none', background: T.white,
    width: '100%', boxSizing: 'border-box', fontFamily: T.font,
  }
  const lbl = { fontSize: 12, fontWeight: 600, color: T.gray600, marginBottom: 5, display: 'block' }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.55)',
      zIndex: 300, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      overflowY: 'auto', padding: '20px 16px',
    }}>
      <div style={{
        background: T.white, borderRadius: 16, width: '100%', maxWidth: 880,
        boxShadow: '0 24px 64px rgba(0,0,0,0.3)', marginBottom: 20,
      }}>
        {/* Modal header */}
        <div style={{
          background: `linear-gradient(135deg, ${T.teal900}, ${T.teal600})`,
          borderRadius: '16px 16px 0 0', padding: '20px 28px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.white, display: 'flex', alignItems: 'center', gap: 10 }}>
              🧾 {isEdit ? `Edit Invoice` : 'New Invoice'}
              {invoice?.invoice_number && <span style={{ fontSize: 14, background: 'rgba(255,255,255,0.18)', borderRadius: 6, padding: '2px 10px' }}>{invoice.invoice_number}</span>}
            </div>
            {clinic?.name && <div style={{ fontSize: 13, color: T.teal200, marginTop: 2 }}>{clinic.name}</div>}
            {clinic?.tagline && <div style={{ fontSize: 11.5, fontWeight: 400, color: T.teal200, opacity: 0.85 }}>{clinic.tagline}</div>}
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
            color: T.white, fontSize: 18, cursor: 'pointer', padding: '5px 12px', lineHeight: 1
          }}>✕</button>
        </div>

        <div style={{ padding: '26px 28px' }}>
          {err && (
            <div style={{
              background: T.red50, border: `1px solid #fecdd3`, color: T.red600,
              borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 14,
            }}>{err}</div>
          )}

          {/* Row 1: meta */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={lbl}>Invoice Number</label>
              <div style={{ ...inp, fontWeight: 700, color: invoice?.invoice_number ? T.teal700 : T.gray400, fontStyle: invoice?.invoice_number ? 'normal' : 'italic', background: T.gray50 }}>
                {invoice?.invoice_number || 'Assigned on save'}
              </div>
            </div>
            <div>
              <label style={lbl}>Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} />
            </div>
            <div>
              <label style={lbl}>Payment Mode</label>
              <select value={payMode} onChange={e => setPayMode(e.target.value)} style={inp}>
                {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* Patient */}
          <div style={{ marginBottom: 20 }}>
            <label style={lbl}>Patient *</label>
            <PatientPicker value={patient} onChange={setPatient} />
          </div>

          {/* Services table */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ ...lbl, marginBottom: 10 }}>Services</label>
            <div style={{ border: `1px solid ${T.gray200}`, borderRadius: 10, overflow: 'hidden' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '2fr 72px 110px 110px 38px',
                background: T.gray50, padding: '8px 12px', gap: 8,
                borderBottom: `1px solid ${T.gray200}`,
                fontSize: 11, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '.4px',
              }}>
                <span>Service</span><span>Qty</span><span style={{ textAlign: 'right' }}>Rate (₹)</span>
                <span style={{ textAlign: 'right' }}>Amount</span><span></span>
              </div>
              {items.map((it, idx) => (
                <div key={idx} style={{
                  display: 'grid', gridTemplateColumns: '2fr 72px 110px 110px 38px',
                  padding: '8px 12px', gap: 8, alignItems: 'center',
                  borderBottom: idx < items.length - 1 ? `1px solid ${T.gray100}` : 'none',
                }}>
                  <select value={it.service} onChange={e => updateItem(idx, 'service', e.target.value)} style={inp}>
                    <option value="">— Select service —</option>
                    {SERVICES.map(s => <option key={s.name}>{s.name}</option>)}
                    {it.service && !SERVICES.some(s => s.name === it.service) && <option>{it.service}</option>}
                  </select>
                  <input type="number" min="1" value={it.qty}
                    onChange={e => updateItem(idx, 'qty', e.target.value)}
                    style={{ ...inp, textAlign: 'center' }} />
                  <input type="number" min="0" value={it.rate}
                    onChange={e => updateItem(idx, 'rate', e.target.value)}
                    style={{ ...inp, textAlign: 'right' }} />
                  <div style={{ textAlign: 'right', fontWeight: 600, color: T.gray800, fontSize: 14 }}>
                    {fmtINR(it.amount)}
                  </div>
                  <button onClick={() => setItems(p => p.filter((_, i) => i !== idx))}
                    style={{
                      background: T.red50, border: 'none', color: T.red600,
                      borderRadius: 6, cursor: 'pointer', padding: '5px 8px', fontSize: 13,
                    }}>✕</button>
                </div>
              ))}
            </div>
            <button onClick={() => setItems(p => [...p, { service: '', qty: 1, rate: 0, amount: 0 }])}
              style={{
                marginTop: 10, padding: '7px 18px',
                background: T.teal50, border: `1px dashed ${T.teal400}`,
                borderRadius: 8, color: T.teal700, cursor: 'pointer', fontSize: 13, fontWeight: 500,
              }}>+ Add Row</button>
          </div>

          {/* Totals + Payment split */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Left: discount, GST, summary */}
            <div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Discount</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select value={discType} onChange={e => setDiscType(e.target.value)}
                    style={{ ...inp, width: 110, flex: '0 0 110px' }}>
                    <option value="flat">₹ Flat</option>
                    <option value="percent">% Percent</option>
                  </select>
                  <input type="number" min="0" value={discVal}
                    onChange={e => setDiscVal(e.target.value)} style={inp} />
                </div>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16 }}>
                <input type="checkbox" checked={gstOn} onChange={e => setGstOn(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: T.teal600 }} />
                <span style={{ fontSize: 14, color: T.gray700 }}>Include GST ({GST_RATE}%)</span>
              </label>

              {/* Summary box */}
              <div style={{
                background: T.gray50, borderRadius: 10, padding: '14px 18px',
                border: `1px solid ${T.gray200}`,
              }}>
                {[
                  ['Subtotal', fmtINR(calc.subtotal)],
                  calc.discount > 0 ? [`Discount${calc.isPct ? ` (${calc.discountValue}%)` : ''}`, `−${fmtINR(calc.discount)}`] : null,
                  calc.gst > 0 ? [`GST (${GST_RATE}%) on ${fmtINR(calc.taxable)}`, fmtINR(calc.gst)] : null,
                ].filter(Boolean).map(([k, v]) => (
                  <div key={k} style={{
                    display: 'flex', justifyContent: 'space-between',
                    marginBottom: 7, color: T.gray600, fontSize: 14,
                  }}>
                    <span>{k}</span><span>{v}</span>
                  </div>
                ))}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  marginTop: 8, paddingTop: 10, borderTop: `2px solid ${T.teal200}`,
                  fontWeight: 800, fontSize: 20, color: T.teal800,
                }}>
                  <span>Total</span><span>{fmtINR(calc.total)}</span>
                </div>
              </div>
            </div>

            {/* Right: payment */}
            <div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Amount Paid (₹)</label>
                <input
                  type="number" min="0" max={calc.total}
                  value={amt.value}
                  onChange={e => amt.set(e.target.value)}
                  style={{ ...inp, fontSize: 18, fontWeight: 700, color: T.gray900, borderColor: calc.overpaid ? '#fca5a5' : T.gray300 }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button onClick={() => amt.set('0')}
                    style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, cursor: 'pointer', background: T.teal50, border: `1px solid ${T.teal200}`, color: T.teal700 }}>
                    Nothing paid
                  </button>
                  <button onClick={amt.resetToTotal}
                    style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, cursor: 'pointer', background: T.teal50, border: `1px solid ${T.teal200}`, color: T.teal700 }}>
                    Full Amount
                  </button>
                </div>
                {calc.overpaid && (
                  <div style={{ fontSize: 12.5, color: T.red600, fontWeight: 600, marginTop: 6 }}>⚠️ {overpaidMessage(calc)}</div>
                )}
              </div>

              {/* Balance box */}
              <div style={{
                background: calc.balance === 0 && calc.total > 0 ? T.green50 : T.amber50,
                border: `1px solid ${calc.balance === 0 && calc.total > 0 ? '#bbf7d0' : '#fde68a'}`,
                borderRadius: 10, padding: '16px 18px',
              }}>
                <div style={{ fontSize: 12, color: T.gray500, fontWeight: 600, marginBottom: 4 }}>Balance Due</div>
                <div style={{
                  fontSize: 30, fontWeight: 900,
                  color: calc.balance === 0 && calc.total > 0 ? T.green600 : T.amber600,
                }}>{fmtINR(calc.balance)}</div>
                <div style={{ marginTop: 10 }}>
                  <StatusBadge status={calc.status} />
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <label style={lbl}>Notes (optional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  rows={3} placeholder="e.g. Follow-up in 4 weeks…"
                  style={{ ...inp, resize: 'vertical', lineHeight: 1.5 }} />
              </div>
            </div>
          </div>

          {/* Action bar */}
          <div style={{
            display: 'flex', gap: 10, marginTop: 24, alignItems: 'center',
            paddingTop: 20, borderTop: `1px solid ${T.gray200}`, flexWrap: 'wrap',
          }}>
            <button onClick={save} disabled={saving || calc.overpaid} style={{
              padding: '10px 26px', background: T.teal700, color: T.white, border: 'none',
              borderRadius: 8, fontWeight: 700, fontSize: 14,
              cursor: saving || calc.overpaid ? 'not-allowed' : 'pointer', opacity: saving || calc.overpaid ? 0.6 : 1,
            }}>
              {saving ? 'Saving…' : '💾 Save Invoice'}
            </button>
            <span style={{ fontSize: 12, color: T.gray400 }}>Print &amp; WhatsApp open after saving, with the final invoice number.</span>
            <button onClick={onClose} style={{
              marginLeft: 'auto', padding: '10px 20px', background: T.gray100, color: T.gray600,
              border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer',
            }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Invoice View Modal ─────────────────────────────────────────────────────────
function InvoiceViewModal({ invoice, onClose, onEdit, canEdit: canEditRole, justSaved }) {
  const { clinic } = useAuth()
  const patient = { full_name: invoice.patient_name, phone: invoice.patient_phone || invoice.patients?.phone }
  const lines    = invLines(invoice)
  const discount = invDiscount(invoice)
  const gst      = invGst(invoice)
  const paid     = invPaid(invoice)
  const balance  = invBalance(invoice)
  const status   = normStatus(invoice)
  const canEdit  = canEditRole && status !== 'paid'

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.55)',
      zIndex: 300, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      overflowY: 'auto', padding: '20px 16px',
    }}>
      <div style={{
        background: T.white, borderRadius: 16, width: '100%', maxWidth: 720,
        boxShadow: '0 24px 64px rgba(0,0,0,0.3)', marginBottom: 20,
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${T.teal900}, ${T.teal600})`,
          borderRadius: '16px 16px 0 0', padding: '20px 26px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.white }}>
              Invoice {invoice.invoice_number || invoice.id.slice(0, 8)}
            </div>
            <div style={{ color: T.teal200, fontSize: 13, marginTop: 2 }}>
              {fmtInvDate(invDate(invoice))} · {invoice.patient_name}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <StatusBadge status={status} />
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
              color: T.white, fontSize: 18, cursor: 'pointer', padding: '5px 12px',
            }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '24px 26px' }}>
          {justSaved && (
            <div style={{ background: T.green50, border: '1px solid #bbf7d0', color: T.green600, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14, fontWeight: 600 }}>
              ✅ Invoice {invoice.invoice_number} saved.
            </div>
          )}

          {/* Services */}
          <div style={{ border: `1px solid ${T.gray200}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '2fr 60px 100px 100px',
              background: T.teal900, padding: '9px 14px', gap: 8,
              fontSize: 12, fontWeight: 700, color: T.teal200, textTransform: 'uppercase',
            }}>
              <span>Service</span><span>Qty</span><span style={{ textAlign: 'right' }}>Rate</span>
              <span style={{ textAlign: 'right' }}>Amount</span>
            </div>
            {lines.map((it, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '2fr 60px 100px 100px',
                padding: '10px 14px', gap: 8, fontSize: 14,
                borderBottom: i < lines.length - 1 ? `1px solid ${T.gray100}` : 'none',
              }}>
                <span style={{ fontWeight: 500 }}>{it.name}</span>
                <span style={{ color: T.gray500, textAlign: 'center' }}>{it.qty}</span>
                <span style={{ color: T.gray500, textAlign: 'right' }}>{fmtINR(it.rate)}</span>
                <span style={{ fontWeight: 600, textAlign: 'right' }}>{fmtINR(it.amount)}</span>
              </div>
            ))}
          </div>

          {/* Financial summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div style={{ background: T.gray50, borderRadius: 10, padding: '16px 18px' }}>
              {[
                ['Subtotal', fmtINR(invoice.subtotal)],
                discount > 0
                  ? [`Discount${invoice.discount_type === 'percent' && Number(invoice.discount_value) ? ` (${invoice.discount_value}%)` : ''}`, `−${fmtINR(discount)}`]
                  : null,
                gst > 0 ? [`GST (${GST_RATE}%)`, fmtINR(gst)] : null,
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, color: T.gray600 }}>
                  <span>{k}</span><span>{v}</span>
                </div>
              ))}
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                paddingTop: 10, borderTop: `2px solid ${T.teal200}`,
                fontWeight: 800, fontSize: 20, color: T.teal800,
              }}>
                <span>Total</span><span>{fmtINR(invoice.total)}</span>
              </div>
            </div>
            <div style={{
              background: balance > 0 ? T.amber50 : T.green50,
              border: `1px solid ${balance > 0 ? '#fde68a' : '#bbf7d0'}`,
              borderRadius: 10, padding: '16px 18px',
            }}>
              {[
                ['Payment Mode', invoice.payment_mode || '—'],
                ['Amount Paid', fmtINR(paid)],
                ['Balance Due', fmtINR(balance)],
              ].map(([k, v]) => (
                <div key={k} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: T.gray500, fontWeight: 600, marginBottom: 2 }}>{k}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.gray900 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          {invoice.notes && (
            <div style={{
              background: T.gray50, borderRadius: 8, padding: '12px 16px',
              fontSize: 13, color: T.gray600, marginBottom: 20,
            }}>
              <strong>Notes:</strong> {invoice.notes}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => printInvoice(invoice, clinic, patient)} style={{
              padding: '10px 20px', background: T.teal700, color: T.white,
              border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600,
            }}>🖨️ Print Invoice</button>
            <button onClick={() => sendInvoiceWhatsApp(invoice, clinic, patient)} style={{
              padding: '10px 20px', background: '#25d366', color: T.white,
              border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer',
            }}>💬 WhatsApp</button>
            {canEdit && (
              <button onClick={onEdit} style={{
                padding: '10px 20px', background: T.white, color: T.teal700,
                border: `1px solid ${T.teal300}`, borderRadius: 8, fontSize: 14, cursor: 'pointer',
              }}>✏️ Edit</button>
            )}
            <button onClick={onClose} style={{
              marginLeft: 'auto', padding: '10px 20px', background: T.gray100, color: T.gray600,
              border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer',
            }}>Close</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main Billing component ─────────────────────────────────────────────────────
export default function Billing() {
  const { clinic, can, role } = useAuth()
  // RLS gives receptionists only today's invoices + invoices with a balance due
  const isReception = role === 'receptionist'
  const RECEPTION_SCOPE = "Showing today's invoices and pending balances."
  const canCreate = can('invoices.create')
  const canEdit   = can('invoices.edit')
  const [invoices, setInvoices]   = useState([])
  const [loading, setLoading]     = useState(true)
  const [loadErr, setLoadErr]     = useState('')
  const [search, setSearch]       = useState('')
  const [statusF, setStatusF]     = useState('all')
  const [dateFrom, setDateFrom]   = useState('')
  const [dateTo, setDateTo]       = useState('')
  const [showForm, setShowForm]   = useState(false)
  const [editInv, setEditInv]     = useState(null)
  const [viewInv, setViewInv]     = useState(null)
  const [justSaved, setJustSaved] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setLoadErr('')
    const { data, error } = await supabase
      .from('invoices')
      .select('*, patients(phone)')
      .order('created_at', { ascending: false })
    if (error) setLoadErr(friendlyError(error))
    setInvoices(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // Derived stats
  const monthPrefix  = todayISO().slice(0, 7)
  const thisMonth    = invoices.filter(inv => invDate(inv).startsWith(monthPrefix))
  const totalRevenue = invoices.reduce((s, i) => s + (Number(i.total) || 0), 0)
  const totalPaid    = invoices.reduce((s, i) => s + invPaid(i), 0)
  const totalPending = invoices.reduce((s, i) => s + invBalance(i), 0)
  const monthRevenue = thisMonth.reduce((s, i) => s + (Number(i.total) || 0), 0)

  // Filtered list
  const filtered = invoices.filter(inv => {
    const d = invDate(inv)
    const q = search.toLowerCase()
    const nameMatch = !q ||
      (inv.patient_name || '').toLowerCase().includes(q) ||
      (inv.invoice_number || '').toLowerCase().includes(q)
    const statusMatch = statusF === 'all' || normStatus(inv) === statusF
    const fromMatch = !dateFrom || d >= dateFrom
    const toMatch   = !dateTo   || d <= dateTo
    return nameMatch && statusMatch && fromMatch && toMatch
  })

  function openEdit(inv) { setViewInv(null); setEditInv(inv); setShowForm(true) }
  function openNew()     { setEditInv(null); setShowForm(true) }
  function closeForm()   { setShowForm(false); setEditInv(null) }
  function onSaved(saved) {
    closeForm()
    setInvoices(prev => {
      const idx = prev.findIndex(i => i.id === saved.id)
      if (idx >= 0) { const n = [...prev]; n[idx] = saved; return n }
      return [saved, ...prev]
    })
    // Show the saved invoice (with its DB-assigned number) so it can be printed / sent
    setJustSaved(true)
    setViewInv(saved)
  }

  const STATUS_FILTERS = [['all', 'All'], ['paid', 'Paid'], ['partial', 'Partial'], ['unpaid', 'Unpaid']]

  return (
    <div style={{
      height: '100%', overflowY: 'auto', background: T.gray100,
      padding: 24, fontFamily: T.font,
    }}>
      {/* Page title + new button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: T.gray900 }}>Billing & Invoices</h2>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: T.gray500 }}>
            {isReception
              ? RECEPTION_SCOPE
              : `${invoices.length} total invoices${clinic?.name ? ` · ${clinic.name}` : ''}`}
          </p>
        </div>
        {canCreate && (
          <button onClick={openNew} style={{
            padding: '10px 22px', background: T.teal700, color: T.white, border: 'none',
            borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer',
            boxShadow: `0 2px 8px ${T.teal700}55`,
          }}>+ New Invoice</button>
        )}
      </div>

      {loadErr && (
        <div style={{ background: T.red50, border: '1px solid #fecdd3', color: T.red600, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14 }}>
          ⚠️ {loadErr}
        </div>
      )}

      {/* Summary cards — revenue totals are not shown to reception */}
      {!isReception && <div style={{ display: 'flex', gap: 14, marginBottom: 22, flexWrap: 'wrap' }}>
        <SummaryCard icon="💰" label="Total Billed"   value={fmtINR(totalRevenue)} accent={T.teal600}  sub={`${invoices.length} invoices`} />
        <SummaryCard icon="✅" label="Collected"      value={fmtINR(totalPaid)}    accent={T.green600} sub="Amount received" />
        <SummaryCard icon="⏳" label="Pending"        value={fmtINR(totalPending)} accent={T.amber600} sub="Balance due" />
        <SummaryCard icon="📅" label="This Month"     value={fmtINR(monthRevenue)} accent={T.blue600}  sub={`${thisMonth.length} invoices`} />
      </div>}

      {/* Filters */}
      <div style={{
        background: T.white, borderRadius: 12, padding: '14px 18px', marginBottom: 18,
        border: `1px solid ${T.gray200}`,
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by patient name or invoice no…"
          style={{
            flex: '1 1 220px', padding: '8px 12px', borderRadius: 8,
            border: `1px solid ${T.gray300}`, fontSize: 14, outline: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          {STATUS_FILTERS.map(([key, label]) => (
            <button key={key} onClick={() => setStatusF(key)} style={{
              padding: '7px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer', fontWeight: 500,
              background: statusF === key ? T.teal700 : T.gray100,
              color: statusF === key ? T.white : T.gray600,
              border: `1px solid ${statusF === key ? T.teal700 : T.gray200}`,
            }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 7, border: `1px solid ${T.gray300}`, fontSize: 13, outline: 'none' }} />
          <span style={{ color: T.gray400, fontSize: 13 }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 7, border: `1px solid ${T.gray300}`, fontSize: 13, outline: 'none' }} />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo('') }}
              style={{ padding: '6px 10px', borderRadius: 7, border: 'none', background: T.gray100, color: T.gray500, cursor: 'pointer', fontSize: 12 }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Invoices table */}
      <div style={{ background: T.white, borderRadius: 12, border: `1px solid ${T.gray200}`, overflow: 'hidden' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '150px 100px 1fr 100px 110px 110px 120px',
          background: T.gray50, padding: '10px 18px', gap: 12,
          fontSize: 11, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '.5px',
          borderBottom: `1px solid ${T.gray200}`,
        }}>
          <span>Invoice No.</span>
          <span>Date</span>
          <span>Patient</span>
          <span style={{ textAlign: 'right' }}>Amount</span>
          <span>Mode</span>
          <span>Status</span>
          <span style={{ textAlign: 'center' }}>Actions</span>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: T.gray400 }}>Loading invoices…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '50px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🧾</div>
            <div style={{ color: T.gray500, fontSize: 15 }}>
              {isReception
                ? (invoices.length === 0 ? RECEPTION_SCOPE : `No invoices match your filters. ${RECEPTION_SCOPE}`)
                : invoices.length === 0 ? (canCreate ? 'No invoices yet. Create your first one!' : 'No invoices yet.') : 'No invoices match your filters.'}
            </div>
          </div>
        ) : (
          filtered.map((inv, i) => {
            const st = normStatus(inv)
            return (
              <div key={inv.id} style={{
                display: 'grid',
                gridTemplateColumns: '150px 100px 1fr 100px 110px 110px 120px',
                padding: '12px 18px', gap: 12, alignItems: 'center',
                borderBottom: i < filtered.length - 1 ? `1px solid ${T.gray100}` : 'none',
                cursor: 'pointer', transition: 'background .12s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = T.teal50}
                onMouseLeave={e => e.currentTarget.style.background = T.white}
                onClick={() => { setJustSaved(false); setViewInv(inv) }}
              >
                <span style={{ fontWeight: 700, color: T.teal700, fontSize: 13 }}>
                  {inv.invoice_number || `#${inv.id.slice(0, 8)}`}
                </span>
                <span style={{ fontSize: 13, color: T.gray600 }}>
                  {fmtInvDate(invDate(inv))}
                </span>
                <span style={{ fontWeight: 500, color: T.gray900, fontSize: 14 }}>
                  {inv.patient_name || '—'}
                </span>
                <span style={{ fontWeight: 700, textAlign: 'right', color: T.gray900 }}>
                  {fmtINR(inv.total)}
                </span>
                <span style={{ fontSize: 13, color: T.gray600 }}>{inv.payment_mode || '—'}</span>
                <StatusBadge status={st} />
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}
                  onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => { setJustSaved(false); setViewInv(inv) }}
                    title="View"
                    style={{
                      padding: '5px 10px', borderRadius: 6, border: `1px solid ${T.gray200}`,
                      background: T.white, cursor: 'pointer', fontSize: 13,
                    }}>👁️</button>
                  {canEdit && st !== 'paid' && (
                    <button
                      onClick={() => openEdit(inv)}
                      title="Edit"
                      style={{
                        padding: '5px 10px', borderRadius: 6, border: `1px solid ${T.teal200}`,
                        background: T.teal50, color: T.teal700, cursor: 'pointer', fontSize: 13,
                      }}>✏️</button>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Modals */}
      {showForm && (
        <InvoiceForm
          invoice={editInv}
          onSave={onSaved}
          onClose={closeForm}
        />
      )}
      {viewInv && !showForm && (
        <InvoiceViewModal
          invoice={viewInv}
          canEdit={canEdit}
          justSaved={justSaved}
          onClose={() => { setViewInv(null); setJustSaved(false) }}
          onEdit={() => openEdit(viewInv)}
        />
      )}
    </div>
  )
}
